// Conversation transcript — JSON document + client-side download for the
// first-party portal chat.
//
// The document shape is deliberately IDENTICAL to the partner widget's
// (partner/widget/src/transcript.ts): the two surfaces render the same
// conversations from the same API, so a customer — or a support engineer
// reading an attached file — should never have to care which chat produced it.
// The two live in separate bundles with no shared package, so the shape is
// mirrored rather than imported; keep them in step.

import type { Citation } from './chatClient';

/** One rendered turn, normalized by the caller from its own message shape. */
export interface TranscriptEntry {
  role: 'user' | 'agent' | 'system';
  /** Display text, already stripped of the invisible control tags. */
  text: string;
  /** True when a HUMAN live agent authored the turn (post-handoff). */
  isLive?: boolean;
  /**
   * Display name of the live agent who authored the turn, when it is known. Used instead of the generic
   * "Live agent" label so the saved conversation names the person the customer spoke to.
   */
  speakerName?: string;
  /** Epoch ms the turn was sent/received; omitted → the turn carries no clock. */
  at?: number;
  citations?: Citation[];
}

/** Conversation-level context stamped into the transcript document. */
export interface TranscriptMeta {
  conversationId: string;
  /** Assistant display name, used to label its turns. */
  title?: string;
  /** Injectable for deterministic tests; defaults to now. */
  generatedAt?: Date;
}

/** One turn as written to the transcript file. */
export interface TranscriptDocumentMessage {
  role: 'user' | 'agent' | 'system';
  /** Who authored the turn. Omitted for `system` asides, which have no speaker. */
  speaker?: string;
  /** Present only on agent turns; true when a HUMAN live agent wrote it. */
  isLiveAgent?: boolean;
  /** ISO-8601. Omitted when the turn carried no timestamp. */
  at?: string;
  text: string;
  citations?: { title: string; url: string }[];
}

/**
 * The saved conversation. Deliberately free of storage detail: this file is for
 * the CUSTOMER, so it carries their conversation and nothing about which CMSP
 * store served it or what that store can hold.
 */
export interface TranscriptDocument {
  conversationId: string;
  downloadedAt: string;
  assistant: string;
  messageCount: number;
  messages: TranscriptDocumentMessage[];
}

const DEFAULT_TITLE = 'Assistant';

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

function speaker(entry: TranscriptEntry, title: string): string | undefined {
  if (entry.role === 'system') return undefined;
  if (entry.role === 'user') return 'You';
  if (!entry.isLive) return title;
  return entry.speakerName?.trim() || 'Live agent';
}

/** Builds the transcript document. Deterministic and free of DOM access. */
export function buildTranscript(
  entries: readonly TranscriptEntry[],
  meta: TranscriptMeta,
): TranscriptDocument {
  const assistant = meta.title?.trim() || DEFAULT_TITLE;
  const generatedAt = meta.generatedAt ?? new Date();

  // Empty turns exist transiently while a reply streams (the typing bubble).
  const messages = entries
    .filter((entry) => entry.text.trim().length > 0)
    .map((entry) => {
      const who = speaker(entry, assistant);
      const citations = entry.citations?.filter((c) => c.url) ?? [];
      return {
        role: entry.role,
        ...(who ? { speaker: who } : {}),
        ...(entry.role === 'agent' ? { isLiveAgent: entry.isLive === true } : {}),
        ...(typeof entry.at === 'number' && Number.isFinite(entry.at)
          ? { at: new Date(entry.at).toISOString() }
          : {}),
        text: entry.text.trim(),
        ...(citations.length > 0
          ? { citations: citations.map((c) => ({ title: c.title, url: c.url })) }
          : {}),
      } as TranscriptDocumentMessage;
    });

  return {
    conversationId: meta.conversationId,
    downloadedAt: generatedAt.toISOString(),
    assistant,
    messageCount: messages.length,
    messages,
  };
}

/** Serializes the conversation as the pretty-printed JSON written to the file. */
export function formatTranscript(
  entries: readonly TranscriptEntry[],
  meta: TranscriptMeta,
): string {
  return `${JSON.stringify(buildTranscript(entries, meta), null, 2)}\n`;
}

/** `transcript-<short id>-<YYYYMMDD-HHMMSS>.json`, safe on every filesystem. */
export function transcriptFileName(meta: TranscriptMeta): string {
  const at = meta.generatedAt ?? new Date();
  const stamp =
    `${at.getFullYear()}${pad2(at.getMonth() + 1)}${pad2(at.getDate())}` +
    `-${pad2(at.getHours())}${pad2(at.getMinutes())}${pad2(at.getSeconds())}`;
  const id = meta.conversationId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8);
  return id ? `transcript-${id}-${stamp}.json` : `transcript-${stamp}.json`;
}

/**
 * Builds the transcript and hands it to the browser as a `.json` download.
 * Returns false when the environment cannot start a download.
 */
export function downloadTranscript(
  entries: readonly TranscriptEntry[],
  meta: TranscriptMeta,
): boolean {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return false;
  }

  let objectUrl: string | null = null;
  try {
    objectUrl = URL.createObjectURL(
      new Blob([formatTranscript(entries, meta)], { type: 'application/json;charset=utf-8' }),
    );
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = transcriptFileName(meta);
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return true;
  } catch {
    return false;
  } finally {
    if (objectUrl) {
      const url = objectUrl;
      // Revoking synchronously can cancel the download in some browsers.
      setTimeout(() => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* The URL may already be gone (e.g. the document was torn down). */
        }
      }, 0);
    }
  }
}
